/**
 * DeleteOrgModal - Moneta-admin confirmation modal for deleting a single organization.
 *
 * Runs a pre-flight impact check (GET /organization/can-delete), shows the impact,
 * blocks deletion when the org has children or default-org users, and requires the
 * admin to type the exact org name before the Delete button enables (double confirmation).
 *
 * For child orgs, report migration options are offered (move to parent / another org / delete).
 * For top-level parent orgs there is no migration target, so reports are permanently deleted.
 *
 * @param {object|null} org - The org being deleted ({ organization, parent, ... }). Null hides the modal.
 * @param {function} onClose - Called to dismiss the modal.
 * @param {function} onDeleted - Called after a successful delete (parent should refresh its list).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, Pressable, ActivityIndicator, TextInput, Modal } from 'react-native';
import { Feather } from '@expo/vector-icons';

import CustomText from '../CustomText';
import AppStyles from '../../AppStyles';
import Util from '../../util/util';
import { Trans, useTranslation } from '../../i18n';

const DeleteOrgModal = ({ org, onClose, onDeleted }) => {
  const { t } = useTranslation(['admin', 'common']);

  const [loadingInfo, setLoadingInfo] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const [confirmText, setConfirmText] = useState('');
  const [migrationStrategy, setMigrationStrategy] = useState('parent');
  const [targetOrganization, setTargetOrganization] = useState(null);

  const orgName = org?.organization;
  const isParent = !org?.parent;

  const resetState = useCallback(() => {
    setDeleteInfo(null);
    setError(null);
    setConfirmText('');
    setMigrationStrategy('parent');
    setTargetOrganization(null);
    setDeleting(false);
  }, []);

  const fetchInfo = useCallback(async () => {
    if (!orgName) return;
    setLoadingInfo(true);
    setError(null);
    try {
      const response = await Util.get('Resty', '/items/organization/can-delete', {
        queryParams: { organization: orgName },
      });
      if (response?.error) {
        setError(response.error);
        setDeleteInfo(null);
      } else {
        setDeleteInfo(response);
      }
    } catch (err) {
      console.error('Error checking org delete eligibility:', err);
      setError(err.message || t('deleteOrg.loadFailed'));
    } finally {
      setLoadingInfo(false);
    }
  }, [orgName]);

  // Re-run the pre-flight check whenever a new org is targeted
  useEffect(() => {
    if (orgName) {
      resetState();
      fetchInfo();
    }
  }, [orgName, resetState, fetchInfo]);

  const handleClose = () => {
    if (deleting) return;
    resetState();
    onClose();
  };

  const handleConfirmDelete = async () => {
    if (!deleteInfo?.can_delete || confirmText !== orgName) return;

    // Require a target when migrating to a specific org
    if (!isParent && migrationStrategy === 'specific' && !targetOrganization) {
      setError(t('deleteOrg.selectTarget'));
      return;
    }

    setDeleting(true);
    setError(null);
    try {
      const queryParams = { organization: orgName };
      // Migration options only apply to child orgs (a top-level parent always deletes reports)
      if (!isParent) {
        queryParams.migration_strategy = migrationStrategy;
        if (migrationStrategy === 'specific' && targetOrganization) {
          queryParams.target_organization = targetOrganization;
        }
      }

      const response = await Util.del('Resty', '/items/organization', { queryParams });
      if (response?.error) {
        setError(response.error);
        return;
      }

      resetState();
      onDeleted(orgName);
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError(err.message || t('deleteOrg.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const canDelete = deleteInfo?.can_delete === true;
  const confirmMatches = confirmText === orgName;
  const reportsCount = deleteInfo?.reports_to_migrate || 0;
  const migrationTargets = (deleteInfo?.available_migration_targets || []).filter(
    (o) => o !== deleteInfo?.parent_organization
  );

  return (
    <Modal visible={!!org} transparent animationType="fade" onRequestClose={handleClose}>
      <Pressable style={styles.overlay} onPress={handleClose}>
        <Pressable style={styles.modal} onPress={(e) => e.stopPropagation()}>
          {/* Header */}
          <View style={styles.header}>
            <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
              {canDelete ? t('deleteOrg.title') : t('deleteOrg.blockedTitle')}
            </CustomText>
            <Pressable
              onPress={handleClose}
              style={({ hovered, focused }) => [
                styles.closeButton,
                { cursor: 'pointer' },
                (hovered || focused) && styles.closeButtonHovered,
              ]}
              accessibilityLabel={t('deleteOrg.closeLabel')}
            >
              <Feather name="x" size={20} color={AppStyles.colorSet.textSecondary} />
            </Pressable>
          </View>

          <View style={styles.subtitleRow}>
            <CustomText size={14} color={AppStyles.colorSet.textSecondary}>
              {orgName}
            </CustomText>
            <View style={[styles.badge, isParent ? styles.badgeParent : styles.badgeChild]}>
              <CustomText size={11} color={isParent ? '#996600' : AppStyles.colorSet.textSecondary}>
                {t(isParent ? 'childOrg.parentBadge' : 'childOrg.childBadge')}
              </CustomText>
            </View>
          </View>

          {/* Loading */}
          {loadingInfo && (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />
              <CustomText style={styles.loadingText} size={14} color={AppStyles.colorSet.textSecondary}>
                {t('deleteOrg.checking')}
              </CustomText>
            </View>
          )}

          {/* Error */}
          {error && !loadingInfo && (
            <View style={styles.errorBox}>
              <Feather name="alert-circle" size={16} color="#DC2626" />
              <CustomText style={styles.errorBoxText} size={13} color="#DC2626">
                {error}
              </CustomText>
            </View>
          )}

          {/* Blocked: has children */}
          {!loadingInfo && deleteInfo && deleteInfo.has_children > 0 && (
            <View style={styles.blockedBox}>
              <Feather name="alert-triangle" size={20} color="#B45309" />
              <View style={styles.blockedTextContainer}>
                <CustomText size={14} font="medium" color="#B45309">
                  {t('deleteOrg.hasChildren', { count: deleteInfo.has_children })}
                </CustomText>
                <CustomText style={styles.blockedSubtext} size={13} color="#92400E">
                  {t('deleteOrg.hasChildrenHint')}
                </CustomText>
              </View>
            </View>
          )}

          {/* Blocked: users have this as default org */}
          {!loadingInfo &&
            deleteInfo &&
            deleteInfo.has_children === 0 &&
            deleteInfo.users_with_default > 0 && (
              <View style={styles.blockedBox}>
                <Feather name="alert-triangle" size={20} color="#B45309" />
                <View style={styles.blockedTextContainer}>
                  <CustomText size={14} font="medium" color="#B45309">
                    {t('deleteOrg.usersWithDefault', { count: deleteInfo.users_with_default })}
                  </CustomText>
                  <CustomText style={styles.blockedSubtext} size={13} color="#92400E">
                    {t('deleteOrg.usersWithDefaultHint')}
                  </CustomText>
                </View>
              </View>
            )}

          {/* Allowed: confirm */}
          {!loadingInfo && canDelete && (
            <View style={styles.confirmContainer}>
              {/* Report migration (child orgs only) */}
              {!isParent && reportsCount > 0 && (
                <View style={styles.migrationBox}>
                  <CustomText size={13} font="medium" color={AppStyles.colorSet.textPrimary}>
                    {t('deleteOrg.reportsInOrg', { count: reportsCount })}
                  </CustomText>

                  <Pressable
                    style={({ hovered, focused }) => [
                      styles.migrationOption,
                      { cursor: 'pointer' },
                      (hovered || focused) && styles.migrationOptionHovered,
                    ]}
                    onPress={() => setMigrationStrategy('parent')}
                  >
                    <View style={[styles.radio, migrationStrategy === 'parent' && styles.radioSelected]}>
                      {migrationStrategy === 'parent' && <View style={styles.radioInner} />}
                    </View>
                    <View style={styles.flex1}>
                      <CustomText size={13} color={AppStyles.colorSet.textPrimary}>
                        {t('deleteOrg.moveToParent', { organization: deleteInfo.parent_organization })}
                      </CustomText>
                      <CustomText size={12} color={AppStyles.colorSet.textSecondary}>
                        {t('deleteOrg.recommended')}
                      </CustomText>
                    </View>
                  </Pressable>

                  {migrationTargets.length > 0 && (
                    <>
                      <Pressable
                        style={({ hovered, focused }) => [
                          styles.migrationOption,
                          { cursor: 'pointer' },
                          (hovered || focused) && styles.migrationOptionHovered,
                        ]}
                        onPress={() => setMigrationStrategy('specific')}
                      >
                        <View
                          style={[styles.radio, migrationStrategy === 'specific' && styles.radioSelected]}
                        >
                          {migrationStrategy === 'specific' && <View style={styles.radioInner} />}
                        </View>
                        <CustomText size={13} color={AppStyles.colorSet.textPrimary}>
                          {t('deleteOrg.moveToOther')}
                        </CustomText>
                      </Pressable>

                      {migrationStrategy === 'specific' && (
                        <View style={styles.targetSelector}>
                          {migrationTargets.map((o) => (
                            <Pressable
                              key={o}
                              style={({ hovered, focused }) => [
                                styles.targetOption,
                                { cursor: 'pointer' },
                                targetOrganization !== o && (hovered || focused) && styles.targetOptionHovered,
                                targetOrganization === o && styles.targetOptionSelected,
                              ]}
                              onPress={() => setTargetOrganization(o)}
                            >
                              <CustomText
                                size={12}
                                color={
                                  targetOrganization === o
                                    ? AppStyles.colorSet.brandPrimary
                                    : AppStyles.colorSet.textSecondary
                                }
                              >
                                {o}
                              </CustomText>
                            </Pressable>
                          ))}
                        </View>
                      )}
                    </>
                  )}

                  <Pressable
                    style={({ hovered, focused }) => [
                      styles.migrationOption,
                      { cursor: 'pointer' },
                      (hovered || focused) && styles.migrationOptionHovered,
                    ]}
                    onPress={() => setMigrationStrategy('delete')}
                  >
                    <View style={[styles.radio, migrationStrategy === 'delete' && styles.radioSelectedDanger]}>
                      {migrationStrategy === 'delete' && <View style={styles.radioInnerDanger} />}
                    </View>
                    <CustomText size={13} color="#DC2626">
                      {t('deleteOrg.deleteReports')}
                    </CustomText>
                  </Pressable>
                </View>
              )}

              {/* Top-level parent: reports are always deleted */}
              {isParent && reportsCount > 0 && (
                <View style={styles.parentReportsWarn}>
                  <Feather name="alert-circle" size={14} color="#DC2626" />
                  <CustomText size={13} color="#DC2626">
                    {t('deleteOrg.reportsWillBeDeleted', { count: reportsCount })}
                  </CustomText>
                </View>
              )}

              {/* Other impacts */}
              {(deleteInfo.routing_rules_affected > 0 ||
                deleteInfo.users_with_access > 0 ||
                deleteInfo.subscriptions_affected > 0) && (
                <View style={styles.impactList}>
                  <CustomText size={13} font="medium" color={AppStyles.colorSet.textPrimary}>
                    {t('deleteOrg.alsoTitle')}
                  </CustomText>
                  {deleteInfo.routing_rules_affected > 0 && (
                    <View style={styles.impactItem}>
                      <Feather name="git-merge" size={14} color={AppStyles.colorSet.textSecondary} />
                      <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                        {t('deleteOrg.removeRoutingRules', { count: deleteInfo.routing_rules_affected })}
                      </CustomText>
                    </View>
                  )}
                  {deleteInfo.users_with_access > 0 && (
                    <View style={styles.impactItem}>
                      <Feather name="users" size={14} color={AppStyles.colorSet.textSecondary} />
                      <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                        {t('deleteOrg.revokeAccess', { count: deleteInfo.users_with_access })}
                      </CustomText>
                    </View>
                  )}
                  {deleteInfo.subscriptions_affected > 0 && (
                    <View style={styles.impactItem}>
                      <Feather name="bell" size={14} color={AppStyles.colorSet.textSecondary} />
                      <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                        {t('deleteOrg.deleteSubscriptions', { count: deleteInfo.subscriptions_affected })}
                      </CustomText>
                    </View>
                  )}
                </View>
              )}

              {/* Type-to-confirm */}
              <View style={styles.confirmTypeBox}>
                <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                  {/* Trans, not t(): the org name is emphasised mid-sentence, and
                      Spanish may not put it in the same position. The <name>
                      placeholder lets the translator move it. */}
                  <Trans
                    i18nKey="admin:deleteOrg.typeToConfirm"
                    values={{ organization: orgName }}
                    components={{
                      name: (
                        <CustomText size={13} font="semiBold" color={AppStyles.colorSet.textPrimary} />
                      ),
                    }}
                  />
                </CustomText>
                <TextInput
                  style={[styles.input, confirmText.length > 0 && !confirmMatches && styles.inputError]}
                  value={confirmText}
                  onChangeText={setConfirmText}
                  placeholder={t('deleteOrg.namePlaceholder')}
                  placeholderTextColor={AppStyles.colorSet.textMuted}
                  autoCapitalize="none"
                  autoCorrect={false}
                  editable={!deleting}
                />
              </View>

              <View style={styles.warningNote}>
                <Feather name="alert-circle" size={14} color="#DC2626" />
                <CustomText size={12} color="#DC2626">
                  {t('deleteOrg.cannotBeUndone')}
                </CustomText>
              </View>
            </View>
          )}

          {/* Buttons */}
          <View style={styles.buttonRow}>
            <Pressable
              style={({ hovered, focused, pressed }) => [
                styles.cancelButton,
                { cursor: 'pointer' },
                (hovered || focused) && styles.cancelButtonHovered,
                pressed && { opacity: 0.7 },
              ]}
              onPress={handleClose}
              disabled={deleting}
            >
              <CustomText size={14} color={AppStyles.colorSet.textSecondary}>
                {canDelete ? t('common:actions.cancel') : t('deleteOrg.close')}
              </CustomText>
            </Pressable>

            {canDelete && (
              <Pressable
                style={({ hovered, focused }) => [
                  styles.deleteButton,
                  { cursor: 'pointer' },
                  confirmMatches && !deleting && (hovered || focused) && styles.deleteButtonHovered,
                  (!confirmMatches || deleting) && styles.deleteButtonDisabled,
                ]}
                onPress={handleConfirmDelete}
                disabled={!confirmMatches || deleting}
                accessibilityLabel={t('deleteOrg.confirmDeleteLabel')}
              >
                {deleting ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <>
                    <Feather name="trash-2" size={16} color="#FFFFFF" />
                    <CustomText style={styles.deleteButtonText} size={14} font="medium" color="#FFFFFF">
                      {t('deleteOrg.delete')}
                    </CustomText>
                  </>
                )}
              </Pressable>
            )}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    width: '100%',
    maxWidth: 440,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  closeButton: {
    padding: 4,
    borderRadius: 8,
  },
  closeButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
  },
  subtitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  badgeParent: {
    backgroundColor: 'rgba(255, 196, 81, 0.2)',
  },
  badgeChild: {
    backgroundColor: '#F3F4F6',
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  loadingText: {
    marginLeft: 10,
  },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FEF2F2',
    borderWidth: 1,
    borderColor: '#FECACA',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    gap: 8,
  },
  errorBoxText: {
    flex: 1,
    lineHeight: 18,
  },
  blockedBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFFBEB',
    borderWidth: 1,
    borderColor: '#FCD34D',
    borderRadius: 8,
    padding: 12,
    gap: 10,
    marginBottom: 8,
  },
  blockedTextContainer: {
    flex: 1,
  },
  blockedSubtext: {
    marginTop: 4,
    lineHeight: 18,
  },
  confirmContainer: {
    marginBottom: 8,
  },
  migrationBox: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 12,
    gap: 10,
    marginBottom: 12,
  },
  migrationOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 4,
  },
  // Web hover/focus feedback — padding cancelled by negative margin (no layout shift)
  migrationOptionHovered: {
    backgroundColor: AppStyles.stateSet.hover,
    borderRadius: 8,
    paddingHorizontal: 6,
    marginHorizontal: -6,
  },
  flex1: {
    flex: 1,
  },
  radio: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSelected: {
    borderColor: AppStyles.colorSet.brandPrimary,
  },
  radioSelectedDanger: {
    borderColor: '#DC2626',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: AppStyles.colorSet.brandPrimary,
  },
  radioInnerDanger: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#DC2626',
  },
  targetSelector: {
    marginLeft: 28,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  targetOption: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E5E5',
  },
  targetOptionHovered: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: AppStyles.stateSet.hover,
  },
  targetOptionSelected: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: '#FFF8E7',
  },
  parentReportsWarn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  impactList: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 12,
    gap: 8,
    marginBottom: 12,
  },
  impactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginLeft: 4,
  },
  confirmTypeBox: {
    gap: 8,
    marginBottom: 12,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E5E5',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: AppStyles.colorSet.textPrimary,
  },
  inputError: {
    borderColor: AppStyles.colorSet.error,
  },
  warningNote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 8,
    gap: 12,
  },
  cancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  cancelButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#DC2626',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  deleteButtonHovered: {
    opacity: 0.92,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 2,
  },
  deleteButtonDisabled: {
    opacity: 0.5,
  },
  deleteButtonText: {
    marginLeft: 6,
  },
});

export default DeleteOrgModal;
